import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaPlus, FaChevronDown, FaChevronRight, FaBan, FaCheck, FaPencilAlt, FaSearch } from "react-icons/fa";
import ApoyoMisionerosModal from "./ApoyoMisionerosModal";
import EditarApoyoModal from "./EditarApoyoModal";
import ReporteMensualMisiones from "./ReporteMensualMisiones";

const PESTANAS = [
  { id: "salidas", etiqueta: "Salidas" },
  { id: "reporte", etiqueta: "Reporte mensual" },
  { id: "recorrido", etiqueta: "Recorrido" },
];

function PestanasMisiones({ activa, onCambiar }) {
  return (
    <div className="flex gap-1 border-b border-slate-200 mb-5">
      {PESTANAS.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onCambiar(p.id)}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
            activa === p.id
              ? "border-indigo-600 text-indigo-700"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          {p.etiqueta}
        </button>
      ))}
    </div>
  );
}

const API = "/api/apoyo-misioneros";

const formatoMoneda = (valor) =>
  `$${Number(valor || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Las fechas llegan como YYYY-MM-DD (sin zona horaria): se formatean sin pasar por Date.
const formatoFecha = (fecha) => {
  if (!fecha) return "";
  const [anio, mes, dia] = fecha.split("-");
  const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${dia} ${meses[Number(mes) - 1]} ${anio}`;
};

// Lista de todo lo registrado con "+ Nueva salida": fecha, título, monto y a quiénes se les
// entregó, con baja/reactivación rápida y edición de cada lote.
function HistorialApoyo({ version, onCambio, onEditar }) {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [estatusFiltro, setEstatusFiltro] = useState("activos");
  const [expandido, setExpandido] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}?estatus=${estatusFiltro}`, { credentials: "include" });
      if (!res.ok) throw new Error("Error obteniendo el historial");
      setLista(await res.json());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [estatusFiltro]);

  useEffect(() => { cargar(); }, [cargar, version]);

  const cambiarEstatus = async (idSalida, activar) => {
    try {
      const res = await fetch(`/api/salidas/${idSalida}${activar ? "/reactivar" : ""}`, {
        method: activar ? "PATCH" : "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Error cambiando el estatus");
      cargar();
      onCambio?.();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold text-slate-800">Detalle de salidas</h2>
        <select
          value={estatusFiltro}
          onChange={(e) => setEstatusFiltro(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
        >
          <option value="activos">Activos</option>
          <option value="inactivos">Inactivos</option>
          <option value="todos">Todos</option>
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-6">Cargando...</p>
      ) : lista.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">
          Todavía no hay salidas registradas. Usa "+ Nueva salida" para registrar la primera.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {lista.map((item) => {
            const abierto = expandido === item.id_salida;
            return (
              <li key={item.id_salida}>
                <button
                  type="button"
                  onClick={() => setExpandido(abierto ? null : item.id_salida)}
                  className="w-full flex items-center gap-3 py-2 text-left hover:bg-slate-50 rounded-lg px-2 -mx-2 transition"
                >
                  <span className="text-slate-400">{abierto ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}</span>
                  <span className="text-sm text-slate-800 font-medium w-24 shrink-0">{formatoFecha(item.fecha)}</span>
                  <span className="text-sm text-slate-600 flex-1">{item.titulo}</span>
                  <span className="text-sm text-slate-500">{item.misioneros.length} misionero{item.misioneros.length === 1 ? "" : "s"}</span>
                  <span className="text-sm font-semibold text-slate-800 tabular-nums w-24 text-right">{formatoMoneda(item.monto_total)}</span>
                  {!item.activo && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactivo</span>}
                  {item.activo && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); onEditar(item); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onEditar(item); } }}
                      title="Editar"
                      className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 cursor-pointer hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                    >
                      <FaPencilAlt className="text-xs" />
                    </span>
                  )}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); cambiarEstatus(item.id_salida, !item.activo); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); cambiarEstatus(item.id_salida, !item.activo); } }}
                    title={item.activo ? "Desactivar" : "Reactivar"}
                    className={`w-7 h-7 flex items-center justify-center rounded-md border cursor-pointer transition ${
                      item.activo
                        ? "border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200"
                        : "border-slate-200 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
                    }`}
                  >
                    {item.activo ? <FaBan className="text-xs" /> : <FaCheck className="text-xs" />}
                  </span>
                </button>
                {abierto && (
                  <ul className="pl-9 pb-2">
                    {item.misioneros.map((m) => (
                      <li key={m.id_misionero} className="flex items-center justify-between text-sm text-slate-600 py-0.5">
                        <span>{m.nombre}</span>
                        <span className="tabular-nums">{formatoMoneda(m.monto)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Recorrido de entregas de cada persona: todos los misioneros del catálogo (se les apoye
// mensualmente o no) más cualquier nombre capturado a mano en "Ofrenda de amor" que no está en el
// catálogo. Se carga el recorrido de cada quien solo al expandirlo.
function RecorridoMisioneros() {
  const [destinatarios, setDestinatarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [expandido, setExpandido] = useState(null);
  const [recorridos, setRecorridos] = useState({});
  const [cargandoRecorrido, setCargandoRecorrido] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/destinatarios`, { credentials: "include" });
        if (!res.ok) throw new Error("Error obteniendo los destinatarios");
        setDestinatarios(await res.json());
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const clave = (d) => (d.tipo === "misionero" ? `m-${d.id_misionero}` : `l-${d.nombre}`);

  const alternar = async (d) => {
    const k = clave(d);
    if (expandido === k) return setExpandido(null);
    setExpandido(k);
    if (recorridos[k]) return;
    setCargandoRecorrido(k);
    try {
      const qs = d.tipo === "misionero" ? `id_misionero=${d.id_misionero}` : `nombre=${encodeURIComponent(d.nombre)}`;
      const res = await fetch(`${API}/recorrido?${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error("Error obteniendo el recorrido");
      const datos = await res.json();
      setRecorridos((r) => ({ ...r, [k]: datos }));
    } catch (error) {
      console.error(error);
      setRecorridos((r) => ({ ...r, [k]: [] }));
    } finally {
      setCargandoRecorrido(null);
    }
  };

  const filtrados = destinatarios.filter((d) => d.nombre.toLowerCase().includes(busqueda.trim().toLowerCase()));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold text-slate-800">Recorrido de entregas</h2>
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre..."
            className="border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-6">Cargando...</p>
      ) : filtrados.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">No se encontró a nadie con ese nombre.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {filtrados.map((d) => {
            const k = clave(d);
            const abierto = expandido === k;
            const items = recorridos[k] || [];
            return (
              <li key={k}>
                <button
                  type="button"
                  onClick={() => alternar(d)}
                  className="w-full flex items-center gap-3 py-2 text-left hover:bg-slate-50 rounded-lg px-2 -mx-2 transition"
                >
                  <span className="text-slate-400">{abierto ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}</span>
                  <span className="text-sm text-slate-800 font-medium flex-1">{d.nombre}</span>
                  {d.tipo === "misionero" ? (
                    <>
                      <span className="text-xs text-slate-500">{d.titulo || "Sin título"}</span>
                      {!d.activo && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactivo</span>}
                    </>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">No registrado</span>
                  )}
                </button>
                {abierto && (
                  <div className="pl-7 pb-2">
                    {cargandoRecorrido === k ? (
                      <p className="text-xs text-slate-400 py-2">Cargando...</p>
                    ) : items.length === 0 ? (
                      <p className="text-xs text-slate-400 py-2">Todavía no se le ha registrado ninguna entrega.</p>
                    ) : (
                      <ul className="divide-y divide-slate-100">
                        {items.map((r) => (
                          <li key={r.id_salida} className="flex items-center justify-between text-sm py-1.5">
                            <div>
                              <p className="text-slate-700">{formatoFecha(r.fecha)}</p>
                              <p className="text-xs text-slate-400">{r.titulo}</p>
                            </div>
                            <span className="font-medium text-slate-700 tabular-nums">{formatoMoneda(r.monto)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function AdminSalidasMisiones() {
  const [pestana, setPestana] = useState("salidas");
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [version, setVersion] = useState(0);
  const [toast, setToast] = useState({ mostrar: false, mensaje: "" });

  const mostrarToast = (mensaje) => {
    setToast({ mostrar: true, mensaje });
    setTimeout(() => setToast({ mostrar: false, mensaje: "" }), 3500);
  };

  const pestanas = <PestanasMisiones activa={pestana} onCambiar={setPestana} />;

  if (pestana === "reporte") {
    return (
      <div className="p-6 bg-slate-50">
        <div className="text-sm text-slate-400 mb-3">
          <Link to="/dashboard/finanzas" className="hover:text-indigo-600">Finanzas</Link>
          <span className="mx-1.5">/</span>
          <span className="text-slate-600 font-medium">Salidas Tesorería Misiones</span>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Salidas Tesorería Misiones</h1>
          <p className="text-sm text-slate-500 mt-0.5">Reporte mensual de entradas, salidas y saldo de la tesorería de misiones.</p>
        </div>

        {pestanas}
        <ReporteMensualMisiones />
      </div>
    );
  }

  if (pestana === "recorrido") {
    return (
      <div className="p-6 bg-slate-50">
        <div className="text-sm text-slate-400 mb-3">
          <Link to="/dashboard/finanzas" className="hover:text-indigo-600">Finanzas</Link>
          <span className="mx-1.5">/</span>
          <span className="text-slate-600 font-medium">Salidas Tesorería Misiones</span>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Salidas Tesorería Misiones</h1>
          <p className="text-sm text-slate-500 mt-0.5">Recorrido de entregas de cada misionero y de quienes recibieron una ofrenda de amor.</p>
        </div>

        {pestanas}
        <RecorridoMisioneros />
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-50">
      {toast.mostrar && (
        <div className="fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white bg-emerald-600">
          {toast.mensaje}
        </div>
      )}

      <div className="text-sm text-slate-400 mb-3">
        <Link to="/dashboard/finanzas" className="hover:text-indigo-600">Finanzas</Link>
        <span className="mx-1.5">/</span>
        <span className="text-slate-600 font-medium">Salidas Tesorería Misiones</span>
      </div>

      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Salidas Tesorería Misiones</h1>
          <p className="text-sm text-slate-500 mt-0.5">Registra y administra el apoyo entregado a los misioneros.</p>
        </div>

        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
        >
          <FaPlus className="text-xs" /> Nueva salida
        </button>
      </div>

      {pestanas}

      <HistorialApoyo version={version} onCambio={() => setVersion((v) => v + 1)} onEditar={setEditando} />

      <ApoyoMisionerosModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onRegistrado={() => { setVersion((v) => v + 1); mostrarToast("Apoyo registrado"); }}
      />
      <EditarApoyoModal
        open={!!editando}
        salida={editando}
        onClose={() => setEditando(null)}
        onGuardado={() => { setVersion((v) => v + 1); mostrarToast("Cambios guardados"); }}
      />
    </div>
  );
}
